/**
 * Elegir los cuadros que corre un club.
 *
 * CINCO GRUPOS Y NO DOS: singles masculino y femenino, dobles masculino,
 * femenino y mixto. La lista sale de `CATEGORY_GROUPS` —el producto de las dos
 * listas del contrato pasado por la regla de qué categoría existe— así que el
 * singles mixto no aparece porque no existe, y no porque acá alguien se acordó
 * de no dibujarlo.
 *
 * Ya no hay montos acá. Marcar una categoría solo dice que el club la corre;
 * lo que cuesta entrar lo decide cada torneo, porque uno grande no cobra lo
 * mismo que el de los martes.
 */
import { useTranslation } from 'react-i18next';
import {
  categoryKey,
  CATEGORY_GROUPS,
  CATEGORY_LEVELS,
  type CommunityCategory,

} from '@tennisup/contracts';

interface CategoryPickerProps {
  value: CommunityCategory[];
  onChange: (categories: CommunityCategory[]) => void;
  disabled?: boolean;
  /** Categorías que no se pueden desmarcar porque tienen jugadores adentro. */
  lockedKeys?: ReadonlySet<string>;
  /**
   * Cuando se elige entre las de un club (al armar un torneo): solo estas se
   * ofrecen. Sin esto se mostrarían las doce, incluidas las que el club no
   * corre y que la API va a rechazar.
   */
  allowed?: readonly CommunityCategory[];
}

export function CategoryPicker({
  value,
  onChange,
  disabled = false,
  lockedKeys,
  allowed,
}: CategoryPickerProps): JSX.Element {
  const { t } = useTranslation();

  /**
   * UN GRUPO POR CUADRO: singles masculino, singles femenino, dobles
   * masculino, dobles femenino y dobles mixto.
   *
   * La lista sale de `CATEGORY_GROUPS`, que es el producto de las dos listas
   * del contrato pasado por la regla de qué categoría existe. El singles mixto
   * no aparece porque la regla dice que no existe, no porque acá alguien se
   * acordó de no dibujarlo.
   */
  const seOfrece = (buscada: CommunityCategory): boolean =>
    allowed === undefined || allowed.some((c) => categoryKey(c) === categoryKey(buscada));

  const marcada = (buscada: CommunityCategory): boolean =>
    value.some((c) => categoryKey(c) === categoryKey(buscada));

  function toggle(cual: CommunityCategory, on: boolean): void {
    const clave = categoryKey(cual);
    onChange(on ? [...value, cual] : value.filter((c) => categoryKey(c) !== clave));
  }

  return (
    <div className="category-picker">
      {CATEGORY_GROUPS.map((grupo) => {
        const niveles = CATEGORY_LEVELS.filter((level) => seOfrece({ ...grupo, level }));
        if (niveles.length === 0) return null;

        const claveGrupo = `${grupo.discipline}_${grupo.sex}`;
        return (
          <fieldset key={claveGrupo} className="category-group">
            {/* UN TEXTO COMPLETO POR CUADRO, no una plantilla con el sexo
                metido adentro. Interpolando salía «Dobles Masculino» al lado
                de «Dobles mixto» —dos mayúsculas distintas— y en inglés daba
                «Doubles Male», que no es inglés: se dice «Men's doubles».
                Cada idioma ordena y escribe esto a su manera. */}
            <legend>{t(`categoryGroup.${claveGrupo}`)}</legend>

            {niveles.map((level) => {
              const categoria: CommunityCategory = { ...grupo, level };
              const id = `cat-${level}-${claveGrupo}`;
              const locked = lockedKeys?.has(categoryKey(categoria)) ?? false;

              return (
                <div className="checkbox" key={id}>
                  <input
                    id={id}
                    type="checkbox"
                    checked={marcada(categoria)}
                    disabled={disabled || locked}
                    onChange={(e) => toggle(categoria, e.target.checked)}
                  />
                  <label htmlFor={id}>
                    {t(`categoryLevel.${level}`)}
                    {locked && <span className="help"> ({t('categories.locked')})</span>}
                  </label>
                </div>
              );
            })}
          </fieldset>
        );
      })}
    </div>
  );
}

/** «₡12 000». Sin decimales, porque el colón no los tiene. */
export function formatColones(amount: number): string {
  return `₡${amount.toLocaleString('es-CR', { maximumFractionDigits: 0 })}`;
}
